import React, { useEffect, useRef, useState } from "react";
import {
  AppstoreOutlined,
  LogoutOutlined,
  MoonOutlined,
  ProfileOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { AppSwitch } from "../../../shared/ui/switch";
import { useThemeMode } from "../../../shared/ui/theme/ThemeProvider";
import {
  MenuActionContent,
  MenuActionIcon,
  MenuDivider,
  MenuHeader,
  MenuHeaderAvatar,
  MenuHeaderName,
  MenuHeaderRole,
  ProfileWrapper,
  UserMenuAction,
  UserMenuTrigger,
  UserTriggerAvatar,
  UserName,
  UserMenu,
  UserMenuLabel,
  UserMenuRow,
} from "./ProfileMenu.styles";
import {
  getAppsPortalUrl,
  redirectToAppsPortal,
} from "../../../shared/auth/lib/appsPortal";

const SSO_APPS_URL = getAppsPortalUrl();

export const ProfileMenu = ({
  displayName = "",
  roleLabel,
  onLogout,
  onEditProfile,
  onEditAnyUser,
  showUserMenu,
}) => {
  const { mode, toggleMode } = useThemeMode();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const shouldShowUserMenu = Boolean(showUserMenu);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const menuItems = [
    { id: "profile", label: "Mi perfil", onClick: onEditProfile },
    { id: "edit-user", label: "Editar un usuario", onClick: onEditAnyUser },
    {
      id: "all-apps",
      label: "Todas las Apps",
      onClick: SSO_APPS_URL ? redirectToAppsPortal : undefined,
    },
    { id: "logout", label: "Cerrar sesión", onClick: onLogout },
  ].filter((item) => item.onClick);

  if (!shouldShowUserMenu) {
    return null;
  }

  return (
    <ProfileWrapper ref={menuRef}>
      <UserMenuTrigger
        type="button"
        onClick={() => setIsMenuOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
      >
        <UserTriggerAvatar size="small" icon={<UserOutlined />} aria-hidden="true" />
        {displayName && <UserName title={displayName}>{displayName}</UserName>}
      </UserMenuTrigger>
      {isMenuOpen && (
        <UserMenu role="menu">
          <MenuHeader>
            <MenuHeaderAvatar size={52} icon={<UserOutlined />} />
            <MenuHeaderName title={displayName}>{displayName}</MenuHeaderName>
            {roleLabel && <MenuHeaderRole>{roleLabel}</MenuHeaderRole>}
          </MenuHeader>

          <MenuDivider />

          <UserMenuRow>
            <MenuActionContent>
              <MenuActionIcon aria-hidden="true">
                <MoonOutlined />
              </MenuActionIcon>
              <UserMenuLabel>Modo oscuro</UserMenuLabel>
            </MenuActionContent>
            <AppSwitch
              size="small"
              checked={mode === "dark"}
              onChange={toggleMode}
            />
          </UserMenuRow>

          <MenuDivider />

          {menuItems.map((item) => (
            <UserMenuAction
              key={item.id}
              type="button"
              onClick={() => {
                setIsMenuOpen(false);
                item.onClick();
              }}
              role="menuitem"
            >
              <MenuActionContent>
                <MenuActionIcon aria-hidden="true">
                  {item.id === "profile" && <ProfileOutlined />}
                  {item.id === "edit-user" && <TeamOutlined />}
                  {item.id === "all-apps" && <AppstoreOutlined />}
                  {item.id === "logout" && <LogoutOutlined />}
                </MenuActionIcon>
                <span>{item.label}</span>
              </MenuActionContent>
            </UserMenuAction>
          ))}
        </UserMenu>
      )}
    </ProfileWrapper>
  );
};
